import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchCurrencies } from '../data-provider';
import type { Currencies, Currency, CurrencyFilters } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal currency lookup cache used to resolve currency references
 * without repeatedly requesting the same currency data.
 */

export const currenciesQueryOptions = (
  filters: Partial<CurrencyFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const currencies = await fetchCurrencies(filters, { signal });
      put(currencies.list);
      return currencies;
    },
    queryKey: CACHE_KEYS.currencies(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_CURRENCIES_CACHE_KEY = btoa('internal-currencies-cache-key');

const INTERNAL_QUERY_CURRENCIES_KEY = currenciesQueryOptions({ id: INTERNAL_CURRENCIES_CACHE_KEY }).queryKey;

const getCurrencyFallback = (currencyCode: string): Currency => {
  logger.warn(`Currency "${currencyCode}" was not found. Verify the data model.`);
  return {
    currencyName: currencyCode,
    id: currencyCode,
    label: currencyCode,
  };
};

export const put = async (currencies: Currency[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CURRENCIES_KEY);

  const newCurrencies = currencies.reduce(
    (acc, currency) => {
      acc[String(currency.id)] = currency;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_CURRENCIES_KEY, {
    list: Object.values(newCurrencies),
    map: newCurrencies,
  });
};

export const getAll = (): Currencies | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_CURRENCIES_KEY);
};

export const find = async (currencyCode: string): Promise<Currency> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CURRENCIES_KEY);

  const currency = data?.map[currencyCode];
  if (currency) {
    return currency;
  }

  try {
    const newData = await queryClient.query(currenciesQueryOptions({ search: { id: currencyCode } }));
    if (newData.map[currencyCode]) {
      void put(newData.list);
      return newData.map[currencyCode];
    }
  } catch {
    return getCurrencyFallback(currencyCode);
  }

  return getCurrencyFallback(currencyCode);
};

export const findAll = async (currencyCodes: string[]): Promise<Currency[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_CURRENCIES_KEY);

  const missingCodes: string[] = [];
  const currencies: Currency[] = [];

  for (const currencyCode of currencyCodes) {
    const currency = data?.map[currencyCode];
    if (currency) {
      currencies.push(currency);
    } else {
      missingCodes.push(currencyCode);
    }
  }

  if (!missingCodes.length) {
    return currencies;
  }

  try {
    const newData = await queryClient.query(currenciesQueryOptions({ search: { id: missingCodes.join(',') } }));
    void put(newData.list);
    for (const missingCode of missingCodes) {
      const currency = newData.map[missingCode];
      if (currency) {
        currencies.push(currency);
      } else {
        currencies.push(getCurrencyFallback(missingCode));
      }
    }
  } catch {
    for (const missingCode of missingCodes) {
      currencies.push(getCurrencyFallback(missingCode));
    }
  }

  return currencies;
};
